import React from "react";

const SLIDE_MS = 450; //Keep in step with the .product-panel transition in home.css

//Product list for a category - a full height panel that slides out from the left, as on the Wix site
const ProductModal = ({ category, onClose }) => {
  const panelRef = React.useRef(null);
  const closeRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);

  //Slide out, then unmount
  const close = React.useCallback(() => {
    setOpen(false);
    window.setTimeout(onClose, SLIDE_MS);
  }, [onClose]);

  React.useEffect(() => {
    const previousFocus = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    closeRef.current.focus();

    //Make the browser apply the off-screen start position first, so the panel slides in from there
    panelRef.current.getBoundingClientRect();
    setOpen(true);

    return () => {
      document.body.style.overflow = overflow;
      if (previousFocus) {
        previousFocus.focus();
      }
    };
  }, []);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        close();
      }
      //Keep keyboard focus inside the panel
      if (e.key === "Tab" && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll("button, a[href]");
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);

  return (
    <div
      className={`product-panel-overlay${open ? " open" : ""}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          close();
        }
      }}
    >
      <div
        ref={panelRef}
        className={"product-panel"}
        role={"dialog"}
        aria-modal={"true"}
        aria-labelledby={"product-panel-title"}
      >
        <div className={"product-panel-header"}>
          <h2 id={"product-panel-title"}>{category.title}</h2>
          <button
            ref={closeRef}
            type={"button"}
            className={"product-panel-close"}
            onClick={close}
          >
            <span className={"visually-hidden"}>Close</span>
            <span aria-hidden={"true"}>×</span>
          </button>
        </div>

        <div className={"product-panel-body"}>
          {category.intro ? <p className={"product-intro"}>{category.intro}</p> : null}
          <ul className={"product-grid"}>
            {category.products.map((p) => (
              <li
                key={`${p.name}-${p.sizes}`}
                className={"product"}
              >
                <h3>
                  {p.name}
                  {p.note ? (
                    <React.Fragment>
                      <br />
                      <span className={"product-note"}>{p.note}</span>
                    </React.Fragment>
                  ) : null}
                </h3>
                <span className={"product-image"}>
                  <img
                    src={p.image}
                    alt={""}
                    width={"200"}
                    height={"200"}
                  />
                </span>
                <p className={"product-sizes"}>{p.sizes}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
